// src/components/Editable — on-page editing building blocks.
//
// On the public site there is no EditContext: <E> renders exactly the element it
// wraps and useEd() returns props that add nothing, so prerendered markup and
// hydration are unchanged. Inside the admin's visual editor (VisualEditor.jsx)
// the context makes text click-to-type and marks photos / list items so the
// editor's floating toolbar can replace or reorder them.
import React, { createContext, useContext, useLayoutEffect, useRef } from "react";
import { renderInline } from "../../utils/inlineMarkup";

const EditContext = createContext(null);
export const EditProvider = EditContext.Provider;

/** Paths may be given as "hero.title" or ["stats", 0, "label"]. */
export const toPath = (p) =>
  Array.isArray(p) ? p : String(p).split(".").map((k) => (/^\d+$/.test(k) ? Number(k) : k));

const NOOP = {
  editing: false,
  img: () => ({}),
  item: () => ({}),
  link: () => ({}),
};

/** Hook for pages: `const ed = useEd();` then spread ed.img(...) / ed.item(...) on elements. */
export function useEd() {
  const ctx = useContext(EditContext);
  if (!ctx) return NOOP;
  return {
    editing: true,
    lang: ctx.lang,
    // A photo (or photo/video "media" object) the editor can replace.
    img: (path, opts = {}) => ({
      "data-ve-img": JSON.stringify({ p: toPath(path), s: opts.shared ? 1 : 0, m: opts.media ? 1 : 0 }),
    }),
    // One item of an editable list (move / duplicate / remove).
    item: (listPath, index, opts = {}) => ({
      "data-ve-item": JSON.stringify({ p: toPath(listPath), i: index, n: opts.name || "item" }),
    }),
    // A link whose destination can be changed (`shared`: one link for both languages).
    link: (path, opts = {}) => ({ "data-ve-link": JSON.stringify({ p: toPath(path), s: opts.shared ? 1 : 0 }) }),
  };
}

// Same text apart from spacing (non-breaking vs normal spaces, runs of whitespace).
const normSpace = (s) => String(s).replace(/[  ]/g, " ").replace(/\s+/g, " ").trim();
const sameText = (a, b) => normSpace(a) === normSpace(b);

const textOf = (children) => {
  if (children == null || children === false) return "";
  if (typeof children === "string" || typeof children === "number") return String(children);
  return null; // not plain text → not editable in place
};

/**
 * Editable text. Public site: renders <Tag {...rest}>{children}</Tag>, unchanged.
 * Editor: the same element, click-to-type (plain text). Changes are saved on blur.
 */
export function E({ as: Tag = "span", path, shared, multiline, max, placeholder, children, ...rest }) {
  const ctx = useContext(EditContext);
  const text = textOf(children);
  if (!ctx || text === null) return <Tag {...rest}>{children}</Tag>;
  return (
    <EditableText Tag={Tag} ctx={ctx} path={toPath(path)} shared={shared} multiline={multiline}
      max={max} placeholder={placeholder} text={text} rest={rest} />
  );
}

/** Paragraphs separated by blank lines (long text). One editable box in the editor. */
export function EParas({ path, value = "", shared, max, pClassName, as: Wrap }) {
  const ctx = useContext(EditContext);
  const paras = String(value).split(/\n\s*\n/).map((p) => p.trim()).filter(Boolean);
  if (!ctx) {
    const ps = paras.map((p, i) => <p key={i} className={pClassName}>{renderInline(p)}</p>);
    return Wrap ? <Wrap>{ps}</Wrap> : <>{ps}</>;
  }
  return (
    <EditableText Tag="div" ctx={ctx} path={toPath(path)} shared={shared} multiline max={max}
      text={paras.join("\n\n")} rest={{ className: pClassName }} />
  );
}

function EditableText({ Tag, ctx, path, shared, multiline, max, placeholder, text, rest }) {
  const ref = useRef(null);

  // React renders no children here; the text is managed directly so typing isn't disturbed.
  useLayoutEffect(() => {
    const el = ref.current;
    if (el && el.ownerDocument.activeElement !== el && el.textContent !== text) el.textContent = text;
  }, [text]);

  const commit = () => {
    const el = ref.current;
    if (!el) return;
    const raw = multiline ? el.innerText : el.textContent;
    // Keep non-breaking spaces (French typography: "100 %"); only tidy ordinary whitespace.
    let value = multiline
      ? raw.replace(/\r/g, "").replace(/[ \t]+\n/g, "\n").replace(/\n{3,}/g, "\n\n").trim()
      : raw.replace(/[ \t\r\n]+/g, " ").trim();
    // Clicking in and out without really changing anything must not save.
    if (sameText(value, text)) {
      if (el.textContent !== text) el.textContent = text;
      return;
    }
    if (max && value.length > max) {
      ctx.notify(`That text is too long (${value.length}/${max} characters). It was shortened.`);
      value = value.slice(0, max);
      el.textContent = value;
    }
    ctx.setText(path, value, { shared });
  };

  const onKeyDown = (e) => {
    if (e.key === "Escape") {
      e.currentTarget.textContent = text;
      e.currentTarget.blur();
    } else if (e.key === "Enter" && !multiline) {
      e.preventDefault();
      e.currentTarget.blur();
    }
  };

  return (
    <Tag
      {...rest}
      ref={ref}
      contentEditable="plaintext-only"
      suppressContentEditableWarning
      spellCheck
      data-ve-text=""
      data-ve-empty={text ? undefined : ""}
      data-ve-placeholder={placeholder || "Click to add text"}
      style={{ ...(rest.style || {}), ...(multiline ? { whiteSpace: "pre-wrap" } : null) }}
      onBlur={commit}
      onKeyDown={onKeyDown}
      onInput={(e) => e.currentTarget.toggleAttribute("data-ve-empty", !e.currentTarget.textContent)}
    />
  );
}

/** Content from another page (e.g. the homepage logo band shown on Contact): not editable here. */
export function NoEdit({ children }) {
  return <EditContext.Provider value={null}>{children}</EditContext.Provider>;
}

/** True inside the visual editor (to show empty optional fields so they can be filled). */
export function useIsEditing() {
  return !!useContext(EditContext);
}
